/**
 * Collects the ids of the multi-select metadata fields as strings, so they can be matched against
 * the raw filter category value. Shared by the list fetch and the async export so both paths agree
 * on which multi-select fields they are dealing with.
 */
export const multiSelectMetadataFieldIds = (
  metadata: Array<{ id: number | string; field_type: string }>
): Set<string> =>
  metadata.reduce<Set<string>>((ids, field) => {
    if (field.field_type === 'multi_select') {
      ids.add(field.id.toString());
    }
    return ids;
  }, new Set());

/**
 * Maps timepoint interval comparators to the comparators the legacy PHP filter
 * validation accepts. The UI represents timepoint filters with interval-suffixed
 * operations (eq_interval, gt_interval, ...), but the backend's FiltersRule only
 * recognises the base comparators.
 */
export const TIMEPOINT_INTERVAL_OPERATION_MAP: Record<string, string> = {
  eq_interval: 'eq',
  gt_interval: 'gt',
  lt_interval: 'lt',
  between_interval: 'between',
};

/**
 * Returns the backend comparator for a timepoint interval operation, or the
 * operation unchanged when no mapping applies. Shared by every path that
 * serializes filters for the legacy sample APIs (list fetch, legacy export,
 * async export) so they cannot drift apart — see BNCH-162468.
 */
export const mapTimepointIntervalOperation = (category: string, operation: string): string => {
  const apiOperation = TIMEPOINT_INTERVAL_OPERATION_MAP[operation];
  return category === 'timepoint' && apiOperation !== undefined ? apiOperation : operation;
};

/**
 * Creates a custom filter processor that converts interval filter operations to their API equivalents.
 * Converts: eq_interval -> eq, gt_interval -> gt, lt_interval -> lt, between_interval -> between
 *
 * @returns Custom filter processor function for use with APITable
 */
export const createIntervalFilterProcessor = () => {
  return (_filters: any[], params: URLSearchParams): void => {
    const filterParams = params.getAll('filters[]');
    const updatedFilters: Array<string> = [];

    filterParams.forEach((param) => {
      const [category, operation, value] = param.split('|');
      const apiOperation = mapTimepointIntervalOperation(category, operation);

      if (apiOperation !== operation) {
        updatedFilters.push(`${category}|${apiOperation}|${value}`);
      } else {
        updatedFilters.push(param);
      }
    });

    // Replace all filter parameters with the updated versions
    params.delete('filters[]');
    updatedFilters.forEach((param) => {
      params.append('filters[]', param);
    });
  };
};

/**
 * Composes multiple filter processors into a single processor
 *
 * @param processors - Array of filter processor functions to apply in sequence
 * @returns Combined filter processor function
 */
export const composeFilterProcessors = (...processors: Array<(filters: any[], params: URLSearchParams) => void>) => {
  return (filters: any[], params: URLSearchParams): void => {
    processors.forEach((processor) => {
      processor(filters, params);
    });
  };
};
